import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from '@/integrations/supabase/client';

interface NewsletterPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NewsletterPopup = ({ open, onOpenChange }: NewsletterPopupProps) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { t, language } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !firstName || !lastName) {
      toast({
        title: language === 'en' ? "Error" : "خطأ",
        description: language === 'en' ? "Please fill in all fields" : "يرجى ملء جميع الحقول",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.functions.invoke('submit-newsletter', {
        body: {
          email: email,
          firstName: firstName,
          lastName: lastName
        }
      });

      if (error) {
        throw error;
      }
      
      toast({
        title: language === 'en' ? "Success!" : "نجح!",
        description: language === 'en' 
          ? "Thank you for subscribing to our newsletter!" 
          : "شكراً لك على الاشتراك في نشرتنا الإخبارية!",
      });
      
      // Close the popup and mark as completed
      onOpenChange(false);
      localStorage.setItem('newsletter-subscribed', 'true');
      
    } catch (error: any) {
      const isAlreadySubscribed = error?.message?.includes('already subscribed');
      
      toast({
        title: language === 'en' 
          ? (isAlreadySubscribed ? "Already subscribed" : "Error")
          : (isAlreadySubscribed ? "مشترك بالفعل" : "خطأ"),
        description: language === 'en' 
          ? (isAlreadySubscribed ? "You're already subscribed to our newsletter." : "Something went wrong. Please try again.")
          : (isAlreadySubscribed ? "أنت مشترك بالفعل في نشرتنا الإخبارية." : "حدث خطأ ما. يرجى المحاولة مرة أخرى."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Mark as dismissed for 12 hours
    localStorage.setItem('newsletter-dismissed', Date.now().toString());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md mx-auto p-0 overflow-hidden">
        <button
          onClick={handleClose}
          className="absolute right-3 top-3 z-10 rounded-full bg-white/90 hover:bg-white w-8 h-8 flex items-center justify-center shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none text-gray-600 hover:text-gray-900"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        
        <div className="bg-white text-black p-6 sm:p-8 text-center relative">
          {/* Logo in top left */}
          <div className="absolute top-4 left-4">
            <img 
              src="/bhg-logo.png" 
              alt="Brassbell Hospitality Group" 
              className="h-4 w-auto" 
              width="78"
              height="16"
              loading="lazy"
            />
          </div>
          
          
          <DialogHeader className="text-center mb-6 mt-8">
            <DialogTitle className="text-2xl font-bold text-black mb-2">
              {language === 'en' ? 'Stay Updated' : 'ابق على اطلاع'}
            </DialogTitle>
            <p className="text-gray-600 text-sm">
              {language === 'en' 
                ? 'Get the latest news about our hospitality projects and exclusive offers'
                : 'احصل على آخر الأخبار حول مشاريعنا في الضيافة والعروض الحصرية'
              }
            </p>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 text-left">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="newsletter-firstName" className="text-black text-sm">
                  {language === 'en' ? 'First Name' : 'الاسم الأول'}
                </Label>
                <Input
                  id="newsletter-firstName"
                  type="text"
                  placeholder={language === 'en' ? 'First name' : 'الاسم الأول'}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="bg-gray-50 border-gray-300 text-black placeholder:text-gray-500 focus:border-gray-500"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newsletter-lastName" className="text-black text-sm">
                  {language === 'en' ? 'Last Name' : 'اسم العائلة'}
                </Label>
                <Input
                  id="newsletter-lastName"
                  type="text"
                  placeholder={language === 'en' ? 'Last name' : 'اسم العائلة'}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="bg-gray-50 border-gray-300 text-black placeholder:text-gray-500 focus:border-gray-500"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newsletter-email" className="text-black text-sm">
                {language === 'en' ? 'Email Address' : 'عنوان البريد الإلكتروني'}
              </Label>
              <Input
                id="newsletter-email"
                type="email"
                placeholder={language === 'en' ? 'Enter your email' : 'أدخل بريدك الإلكتروني'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-50 border-gray-300 text-black placeholder:text-gray-500 focus:border-gray-500"
                required
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-black text-white hover:bg-gray-800 font-medium"
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? (language === 'en' ? 'Subscribing...' : 'جار الاشتراك...') 
                : (language === 'en' ? 'Subscribe Now' : 'اشترك الآن')
              }
            </Button>
          </form>

          <p className="text-xs text-gray-500 mt-4">
            {language === 'en' 
              ? 'We respect your privacy. Unsubscribe at any time.'
              : 'نحن نحترم خصوصيتك. يمكنك إلغاء الاشتراك في أي وقت.'
            }
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterPopup;